/**
 * @fileoverview Reusable modal component for displaying training requirement warnings
 * Used when users need to complete training before performing certain actions
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { DmsModal, DmsButton, DmsAlertWarning, intl } from "dmsbasereact";

/**
 * TrainingModal - Modal component for displaying training requirement warnings
 * 
 * @example
 * <TrainingModal
 *   isVisible={showTrainingModal}
 *   onClose={() => setShowTrainingModal(false)}
 *   message="You must complete the required training..."
 *   instructionText="Please navigate to the Training Materials section..."
 * />
 */
export const TrainingModal = ({
  isVisible,
  onClose,
  message = "",
  instructionText = "",
  title = null,
}) => {
  return (
    <DmsModal
      isVisible={isVisible}
      onClose={onClose}
    >
      <DmsModal.Header
        title={title || intl.get("training_required").d("Training Required")}
        onClose={onClose}
      />
      <DmsModal.Body>
        <Box sx={{ p: 2 }}>
          {message && (
            <DmsAlertWarning
              message={message}
            />
          )}
          {instructionText && (
            <Typography sx={{ mt: 2 }}>
              {instructionText}
            </Typography>
          )}
        </Box>
      </DmsModal.Body>
      <DmsModal.Footer>
        <DmsButton
          variant="primary"
          text={intl.get("ok").d("OK")}
          onClick={onClose}
        />
      </DmsModal.Footer>
    </DmsModal>
  );
};

TrainingModal.propTypes = {
  /** Whether the modal is visible */
  isVisible: PropTypes.bool.isRequired,
  /** Callback function when modal is closed */
  onClose: PropTypes.func.isRequired,
  /** Warning message to display */
  message: PropTypes.string,
  /** Additional instruction text */
  instructionText: PropTypes.string,
  /** Custom title for the modal (defaults to "Training Required") */
  title: PropTypes.string,
};

